import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  ArrowLeft, 
  Wallet,
  Send, 
  Check, 
  DollarSign,
  LogOut,
  ExternalLink,
  AlertCircle,
  Loader2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useWallet } from '@/contexts/WalletContext';
import { getUSDCBalance, sendUSDC, waitForTransaction, getPolygonscanLink, formatAddress } from '@/utils/blockchain';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CryptoTransferPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const wallet = useWallet();
  const location = useLocation();
  
  const [step, setStep] = useState(1); // 1: Setup, 2: Sending, 3: Success
  const [usdcBalance, setUsdcBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);
  
  // Pre-fill from chat if data passed
  const chatData = location.state;
  
  const [transferData, setTransferData] = useState({
    amount: chatData?.amount?.toString() || '',
    recipientAddress: '',
    recipientName: ''
  });

  const [feePreview, setFeePreview] = useState(null);
  const [txHashes, setTxHashes] = useState({
    main: null,
    fee: null
  });

  useEffect(() => {
    if (wallet.isConnected && wallet.chainId === 137) {
      loadUSDCBalance();
    }
  }, [wallet.isConnected, wallet.account, wallet.chainId]);

  useEffect(() => {
    if (transferData.amount && parseFloat(transferData.amount) > 0) {
      calculateFees();
    }
  }, [transferData.amount]);

  const loadUSDCBalance = async () => {
    setLoadingBalance(true);
    try {
      const balance = await getUSDCBalance(wallet.account);
      setUsdcBalance(balance);
    } catch (error) {
      console.error('Failed to load USDC balance:', error);
      toast.error('Failed to load USDC balance');
    } finally {
      setLoadingBalance(false);
    }
  };

  const calculateFees = async () => {
    try {
      const amount = parseFloat(transferData.amount);
      const response = await axios.get(`${API}/calculate-fee?amount=${amount}`);
      setFeePreview(response.data);
    } catch (error) {
      console.error('Failed to calculate fees:', error);
    }
  };

  const handleSendCrypto = async () => {
    if (!wallet.isConnected) {
      toast.error('Please connect your wallet first');
      return;
    }

    if (!transferData.recipientAddress || !transferData.amount) {
      toast.error('Please fill in all fields');
      return;
    }

    if (wallet.chainId !== 137) {
      toast.info('Please switch to Polygon network');
      try {
        await wallet.switchToPolygon();
      } catch (error) {
        toast.error('Failed to switch network');
        return;
      }
    }

    setStep(2);

    try {
      const amount = parseFloat(transferData.amount);
      const serviceFee = feePreview.service_fee;
      
      // Step 1: Send service fee to RouteFlow
      toast.info('Step 1/2: Sending service fee...');
      const feeResult = await sendUSDC(wallet.TREASURY_ADDRESS, serviceFee.toFixed(6));
      setTxHashes(prev => ({ ...prev, fee: feeResult.hash }));
      
      toast.info('Confirming fee transaction...');
      await waitForTransaction(feeResult.hash, 1);
      
      // Step 2: Send main amount to recipient
      toast.info('Step 2/2: Sending transfer to recipient...');
      const mainResult = await sendUSDC(transferData.recipientAddress, amount.toFixed(6));
      setTxHashes(prev => ({ ...prev, main: mainResult.hash }));
      
      toast.info('Confirming main transaction...');
      await waitForTransaction(mainResult.hash, 1);
      
      // Save transaction to backend
      const transactionData = {
        amount: amount,
        from_currency: 'USDC',
        to_currency: 'USDC',
        from_country: 'Polygon',
        to_country: 'Polygon',
        recipient_name: transferData.recipientName,
        recipient_account: transferData.recipientAddress,
        tx_hash: mainResult.hash,
        fee_tx_hash: feeResult.hash
      };

      await axios.post(`${API}/transactions?user_id=${user.id}`, transactionData);
      
      toast.success('Transfer completed successfully!');
      setStep(3);
      
    } catch (error) {
      console.error('Transfer failed:', error);
      toast.error(`Transfer failed: ${error.message}`);
      setStep(1);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50'>
      {/* Navigation */}
      <nav className='bg-white border-b shadow-sm'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex justify-between items-center h-16'>
            <div className='flex items-center space-x-4'>
              <Button variant='ghost' size='icon' onClick={() => navigate('/dashboard')}>
                <ArrowLeft className='w-5 h-5' />
              </Button>
              <div className='flex items-center space-x-3'>
                <img 
                  src='https://customer-assets.emergentagent.com/job_smart-money-route/artifacts/6teoyzj7_Generated%20Image%20November%2018%2C%202025%20-%203_06PM.png' 
                  alt='RouteFlow AI' 
                  className='h-10 w-auto'
                />
                <span className='text-xl font-bold gradient-text'>Crypto Transfer</span>
              </div>
            </div>
            <Button variant='ghost' onClick={onLogout}>
              <LogOut className='w-4 h-4 mr-2' /> Logout
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className='max-w-2xl mx-auto px-4 py-8'>
        
        {/* Step 1: Setup Transfer */}
        {step === 1 && (
          <div className='space-y-6'>
            {/* Wallet Connection */}
            <Card className='border-0 shadow-lg'>
              <CardHeader>
                <CardTitle className='flex items-center space-x-2'>
                  <Wallet className='w-6 h-6 text-emerald-600' />
                  <span>Wallet Connection</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!wallet.isConnected ? (
                  <div className='text-center py-6'>
                    <p className='text-gray-600 mb-4'>Connect your MetaMask wallet to send USDC</p>
                    <Button 
                      onClick={wallet.connectWallet}
                      disabled={wallet.connecting}
                      className='bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700'
                    >
                      {wallet.connecting ? 'Connecting...' : 'Connect Wallet'}
                    </Button>
                  </div>
                ) : (
                  <div className='space-y-4'>
                    <div className='flex items-center justify-between bg-gray-50 rounded-lg p-4'>
                      <div>
                        <p className='text-sm text-gray-600'>Connected Address</p>
                        <p className='font-mono font-semibold'>{formatAddress(wallet.account)}</p>
                      </div>
                      <div className='text-right'>
                        <p className='text-sm text-gray-600'>USDC Balance</p>
                        <p className='text-lg font-bold text-emerald-600'>
                          {loadingBalance ? 'Loading...' : `${parseFloat(usdcBalance || 0).toFixed(2)} USDC`}
                        </p>
                      </div>
                    </div>
                    
                    {wallet.chainId !== 137 && (
                      <Alert>
                        <AlertCircle className='h-4 w-4' />
                        <AlertDescription>
                          Please switch to Polygon network to continue
                          <Button 
                            variant='outline' 
                            size='sm' 
                            className='ml-2'
                            onClick={wallet.switchToPolygon}
                          >
                            Switch Network
                          </Button>
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {wallet.isConnected && wallet.chainId === 137 && (
              <Card className='border-0 shadow-lg'>
                <CardHeader>
                  <CardTitle className='flex items-center space-x-2'>
                    <Send className='w-6 h-6 text-emerald-600' />
                    <span>Transfer Details</span>
                  </CardTitle>
                  <CardDescription>Send USDC on Polygon network</CardDescription>
                </CardHeader>
                <CardContent className='space-y-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='recipient-name'>Recipient Name</Label>
                    <Input
                      id='recipient-name'
                      placeholder='John Doe'
                      value={transferData.recipientName}
                      onChange={(e) => setTransferData({ ...transferData, recipientName: e.target.value })}
                    />
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='recipient-address'>Recipient Wallet Address (Polygon)</Label>
                    <Input
                      id='recipient-address'
                      placeholder='0x...'
                      value={transferData.recipientAddress}
                      onChange={(e) => setTransferData({ ...transferData, recipientAddress: e.target.value })}
                      className='font-mono'
                    />
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='amount'>Amount (USDC)</Label>
                    <div className='relative'>
                      <span className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-500'>
                        USDC
                      </span>
                      <Input
                        id='amount'
                        type='number'
                        step='0.01'
                        placeholder='0.00'
                        value={transferData.amount}
                        onChange={(e) => setTransferData({ ...transferData, amount: e.target.value })}
                        className='pl-16 text-xl h-12 font-semibold'
                      />
                    </div>
                    {usdcBalance && (
                      <p className='text-sm text-gray-600'>
                        Available: {parseFloat(usdcBalance).toFixed(2)} USDC
                      </p>
                    )}
                  </div>

                  {feePreview && (
                    <div className='bg-gray-50 rounded-lg p-4 space-y-2'>
                      <div className='flex justify-between text-sm'>
                        <span className='text-gray-600'>Transfer Amount</span>
                        <span className='font-semibold'>{parseFloat(transferData.amount).toFixed(2)} USDC</span>
                      </div>
                      <div className='flex justify-between text-sm'>
                        <span className='text-gray-600'>Service Fee ({feePreview.fee_percentage}%)</span>
                        <span className='font-semibold text-emerald-600'>{feePreview.service_fee.toFixed(2)} USDC</span>
                      </div>
                      <div className='flex justify-between text-sm'>
                        <span className='text-gray-600'>Gas Fee (est.)</span>
                        <span className='font-semibold'>~0.01 USDC</span>
                      </div>
                      <div className='border-t pt-2 flex justify-between'>
                        <span className='font-semibold'>Total Cost</span>
                        <span className='font-bold text-lg'>{(feePreview.service_fee + parseFloat(transferData.amount) + 0.01).toFixed(2)} USDC</span>
                      </div>
                    </div>
                  )}

                  <Alert>
                    <AlertCircle className='h-4 w-4' />
                    <AlertDescription>
                      This is a real blockchain transaction. Make sure the recipient address is correct!
                    </AlertDescription>
                  </Alert>

                  <Button 
                    className='w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700'
                    onClick={handleSendCrypto}
                    disabled={!transferData.amount || !transferData.recipientAddress || !transferData.recipientName}
                  >
                    <Send className='w-4 h-4 mr-2' /> Send USDC
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Step 2: Processing */}
        {step === 2 && (
          <Card className='border-0 shadow-lg'>
            <CardContent className='text-center py-12'>
              <Loader2 className='w-16 h-16 animate-spin text-emerald-600 mx-auto mb-4' />
              <h3 className='text-2xl font-bold mb-2'>Processing Transfer</h3>
              <p className='text-gray-600 mb-6'>Please wait while we process your blockchain transactions...</p>
              
              {txHashes.fee && (
                <div className='bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4'>
                  <p className='text-sm text-emerald-900 font-semibold mb-2'>✓ Fee Transaction Sent</p>
                  <a 
                    href={getPolygonscanLink(txHashes.fee)} 
                    target='_blank' 
                    rel='noopener noreferrer'
                    className='text-sm text-emerald-700 hover:underline flex items-center justify-center space-x-1'
                  >
                    <span>View on Polygonscan</span>
                    <ExternalLink className='w-3 h-3' />
                  </a>
                </div>
              )}
              
              {txHashes.main && (
                <div className='bg-emerald-50 border border-emerald-200 rounded-lg p-4'>
                  <p className='text-sm text-emerald-900 font-semibold mb-2'>✓ Main Transaction Sent</p>
                  <a 
                    href={getPolygonscanLink(txHashes.main)} 
                    target='_blank' 
                    rel='noopener noreferrer'
                    className='text-sm text-emerald-700 hover:underline flex items-center justify-center space-x-1'
                  >
                    <span>View on Polygonscan</span>
                    <ExternalLink className='w-3 h-3' />
                  </a>
                </div>
              )}
              
              <p className='text-xs text-gray-500 mt-6'>Do not close this window</p>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Success */}
        {step === 3 && (
          <Card className='border-0 shadow-lg'>
            <CardContent className='text-center py-12'>
              <div className='w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4'>
                <Check className='w-12 h-12 text-emerald-600' />
              </div>
              <h3 className='text-3xl font-bold mb-2'>Transfer Complete!</h3>
              <p className='text-gray-600 mb-6'>
                Your USDC has been successfully sent to {formatAddress(transferData.recipientAddress)}
              </p>

              <div className='bg-gray-50 rounded-lg p-4 mb-6 space-y-3'>
                <div className='flex justify-between'>
                  <span className='text-gray-600'>Amount Sent</span>
                  <span className='font-semibold'>{parseFloat(transferData.amount).toFixed(2)} USDC</span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-gray-600'>Service Fee</span>
                  <span className='font-semibold'>{feePreview?.service_fee.toFixed(2)} USDC</span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-gray-600'>Recipient</span>
                  <span className='font-semibold'>{transferData.recipientName}</span>
                </div>
              </div>

              <div className='space-y-2 mb-6'>
                <a 
                  href={getPolygonscanLink(txHashes.main)} 
                  target='_blank' 
                  rel='noopener noreferrer'
                  className='block'
                >
                  <Button variant='outline' className='w-full'>
                    View Transaction <ExternalLink className='w-4 h-4 ml-2' />
                  </Button>
                </a>
              </div>

              <Button 
                className='w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700'
                onClick={() => navigate('/transactions')}
              >
                View All Transactions
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CryptoTransferPage;
